/**
 * BRAND-SCOPED ITEM QUERY
 *
 * Shared helper for finding all user items attributed to a brand,
 * regardless of which brand field (brandSlug / brandLower / slug) is
 * populated on the item docs.
 *
 * Motivation: legacy items have inconsistent brand-side field
 * populating. Brands with punctuation (e.g. "Polish ALL the Things!")
 * end up with brandLower values that don't match the exact `brand.name`
 * lowercased. Filtering only by brandLower would miss those.
 *
 * Strategy — most-reliable to least:
 *   1. Query polishCatalog by brandSlug — get every canonical polish
 *      slug attributed to this brand.
 *   2. Query items by `slug in [chunk]` — batched 10 at a time
 *      (Firestore `in` limit).
 *   3. Belt-and-suspenders fallback: also union in results from
 *      brandSlug and brandLower filters on items directly. Cheap when
 *      they return nothing; catches items whose `slug` field is
 *      missing but whose brand fields are present.
 *
 * Duplicates are removed by doc path. Callers receive the raw
 * QueryDocumentSnapshot list to filter/aggregate as needed.
 */

import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  type QueryDocumentSnapshot,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { slugify } from './polishSlug';

export async function fetchBrandUserItems(
  brandName: string
): Promise<QueryDocumentSnapshot[]> {
  const brand = brandName.trim();
  if (!brand) return [];
  const brandLower = brand.toLowerCase();
  const brandSlug = slugify(brand);
  if (!brandSlug) return [];

  const seen = new Set<string>();
  const merged: QueryDocumentSnapshot[] = [];
  const push = (docs: QueryDocumentSnapshot[]) => {
    for (const d of docs) {
      // Only user items — brand items live under brands/{id}/items/ and
      // are managed separately.
      if (d.ref.parent.parent?.parent?.id !== 'users') continue;
      if (!seen.has(d.ref.path)) {
        seen.add(d.ref.path);
        merged.push(d);
      }
    }
  };

  // 1) polishCatalog docs for this brand → get canonical polish slugs.
  const catalogSnap = await getDocs(
    query(collection(db, 'polishCatalog'), where('brandSlug', '==', brandSlug))
  );
  const polishSlugs = catalogSnap.docs.map((d) => d.id);

  // 2) items by slug (chunked at 10 for Firestore `in` limit).
  if (polishSlugs.length > 0) {
    const chunks: string[][] = [];
    for (let i = 0; i < polishSlugs.length; i += 10) {
      chunks.push(polishSlugs.slice(i, i + 10));
    }
    const results = await Promise.all(
      chunks.map((chunk) =>
        getDocs(query(collectionGroup(db, 'items'), where('slug', 'in', chunk)))
          .then((s) => s.docs)
          .catch(() => [])
      )
    );
    for (const batch of results) push(batch);
  }

  // 3) Fallback queries — catch items whose slug field is missing
  // but whose brand fields match.
  const fallbacks = await Promise.all([
    getDocs(query(collectionGroup(db, 'items'), where('brandSlug', '==', brandSlug)))
      .then((s) => s.docs)
      .catch(() => []),
    getDocs(query(collectionGroup(db, 'items'), where('brandLower', '==', brandLower)))
      .then((s) => s.docs)
      .catch(() => []),
  ]);
  for (const batch of fallbacks) push(batch);

  return merged;
}
